import { AjaxWrapper, HttpMethod } from '../../generic/ajaxWrapper'
import { generateGuid } from 'utils'

declare const RASA_CAMPAIGNS_API_BASE_URL: string

interface BlastData {
  id?: number
  name?: string
  description?: string
  subject?: string
  preview?: string
  start_date?: Date | null
  sequence_id?: number
  campaign_id?: number
}

interface NewsletterList {
  id: number
  name: string
}

interface CampaignDataSourceConfig {
  baseUrl: string,
  datasetMethod: 'GET' | 'POST' | 'PUT' | 'DELETE',
  entityMethod: 'GET' | 'POST' | 'PUT' | 'DELETE',
  buildDatasetUrl: (baseUrl: string, datasetName: string, queryParams: Record<string, any>) => string,
  buildEntityUrl: (baseUrl: string, entityName: string, entityId: string | number, queryParams: Record<string, any>) => string
}

const CAMPAIGN_DATA_SOURCE_CONFIG: CampaignDataSourceConfig = {
  baseUrl: RASA_CAMPAIGNS_API_BASE_URL || 'https://campaigns-api-dev.rasa.io',
  datasetMethod: 'POST',
  entityMethod: 'POST',
  buildDatasetUrl: (baseUrl, datasetName, queryParams) => {
    const queryString = Object.keys(queryParams)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
      .join('&')
    return `${baseUrl}/api/dataset/${datasetName}${queryString ? '?' + queryString : ''}`
  },
  buildEntityUrl: (baseUrl, entityName, entityId, queryParams) => {
    const queryString = Object.keys(queryParams)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
      .join('&')
    return `${baseUrl}/api/entity/${entityName}/${entityId}${queryString ? '?' + queryString : ''}`
  }
}

export class BlastsAPI {
  private static getConfig() {
    return CAMPAIGN_DATA_SOURCE_CONFIG
  }

  private static getCustomHeaders(accountGuid: string) {
    return {
      'X-Account-Guid': accountGuid
    }
  }

  /**
   * Make an API call with custom headers
   */
  private static apiCall(
    accountGuid: string,
    url: string,
    method: HttpMethod,
    payload: any = null,
    contentType: string = 'application/json'
  ): Promise<any> {
    const customHeaders = this.getCustomHeaders(accountGuid)
    return AjaxWrapper.ajax(url, method, payload, contentType, customHeaders)
  }

  /**
   * Fetch all lists from the campaigns API
   */
  static fetchLists(accountGuid: string): Promise<any[]> {
    const config = this.getConfig()
    const method = HttpMethod[config.datasetMethod]
    const listUrl = config.buildDatasetUrl(config.baseUrl, 'list', {})
    const listPayload = {
      search: {
        is_archived: 0
      }
    }

    return this.apiCall(accountGuid, listUrl, method, listPayload)
  }

  /**
   * Fetch all blasts from the campaigns API
   */
  static fetchBlasts(accountGuid: string): Promise<any[]> {
    const config = this.getConfig()
    const method = HttpMethod[config.datasetMethod]
    const blastUrl = config.buildDatasetUrl(config.baseUrl, 'blast', {})

    return this.apiCall(accountGuid, blastUrl, method)
  }

  /**
   * Fetch lists and blasts in parallel
   */
  static fetchListsAndBlasts(accountGuid: string): Promise<[any[], any[]]> {
    return Promise.all([
      this.fetchLists(accountGuid),
      this.fetchBlasts(accountGuid)
    ])
  }

  /**
   * Create a new blast
   */
  static createBlast(
    accountGuid: string,
    communityIdentifier: string,
    blastName: string,
    blastDescription: string,
    newsletterList: NewsletterList,
    templateGuid: string,
    subject?: string,
    preview?: string
  ): Promise<void> {
    const config = this.getConfig()
    const method = HttpMethod[config.datasetMethod]
    const url = config.buildEntityUrl(config.baseUrl, 'blast', '', {})

    const templateValue = `${communityIdentifier}/${templateGuid}`

    const payload: any = {
      name: blastName,
      description: blastDescription,
      lists: [{
        id: newsletterList.id,
        name: newsletterList.name,
      }],
      purpose: 'Newsletter Blast',
      template: templateValue,
      subject: {
        type: 'specific',
        content: (subject || '').trim(),
        preview: (preview || '').trim()
      }
    }

    return this.apiCall(accountGuid, url, method, payload)
  }

  /**
   * Update an existing blast
   */
  static updateBlast(
    accountGuid: string,
    blastId: number,
    blastData: BlastData
  ): Promise<void> {
    const payload: any = {
      name: blastData.name,
      description: blastData.description,
      subject: {
        type: 'specific',
        content: (blastData.subject || '').trim(),
        preview: (blastData.preview || '').trim()
      }
    }

    return this.updateBlastWithPayload(accountGuid, blastId, payload)
  }

  /**
   * Schedule a blast (only updates the send date)
   */
  static scheduleBlast(
    accountGuid: string,
    blastId: number,
    startDate: Date | null
  ): Promise<void> {
    const payload: any = {
      start_date: startDate,
    }

    return this.updateBlastWithPayload(accountGuid, blastId, payload)
  }

  /**
   * Common method to update a blast with given payload
   */
  private static updateBlastWithPayload(
    accountGuid: string,
    blastId: number,
    payload: any
  ): Promise<void> {
    const config = this.getConfig()
    const url = config.buildEntityUrl(config.baseUrl, 'blast', blastId, {})

    return this.apiCall(accountGuid, url, HttpMethod.PUT, payload)
  }

  /**
   * Get the empty campaigns template
   */
  static getEmptyCampaignsTemplate(accountGuid: string): Promise<any> {
    const config = this.getConfig()
    const url = `${config.baseUrl}/api/services/template-editor/templates/campaigns_empty`

    return this.apiCall(accountGuid, url, HttpMethod.GET)
  }

  /**
   * Save a blast template
   */
  static saveBlastTemplate(
    accountGuid: string,
    templateGuid: string,
    blastName: string,
    emptyTemplate: any
  ): Promise<void> {
    const config = this.getConfig()
    const url = `${config.baseUrl}/api/services/template-editor/save`

    const savePayload = {
      ...emptyTemplate,
      filename: templateGuid,
      isNew: true,
      metadata: {
        templateType: 'blast',
        displayName: blastName,
      },
    }

    return this.apiCall(accountGuid, url, HttpMethod.POST, savePayload)
  }

  /**
   * Create a blast template (gets empty template and saves it with metadata)
   */
  static createBlastTemplate(
    accountGuid: string,
    blastName: string
  ): Promise<{ templateGuid: string }> {
    const templateGuid = generateGuid()

    // Step 1: Get empty template
    return this.getEmptyCampaignsTemplate(accountGuid)
      .then((emptyTemplate) => {
        // Step 2: Save the template
        return this.saveBlastTemplate(accountGuid, templateGuid, blastName, emptyTemplate)
      })
      .then(() => {
        return { templateGuid }
      })
  }

  /**
   * Send a blast immediately
   */
  static sendBlastNow(
    accountGuid: string,
    blastId: number,
    sequenceId: number
  ): Promise<void> {
    const config = this.getConfig()
    const url = `${config.baseUrl}/api/services/send/message`

    const payload = {
      sequence_id: sequenceId,
      message_id: blastId
    }

    return this.apiCall(accountGuid, url, HttpMethod.POST, payload)
  }

  /**
   * Fetch contacts from a list
   */
  static fetchContacts(
    accountGuid: string,
    listId: number
  ): Promise<any[]> {
    const config = this.getConfig()
    const method = HttpMethod[config.datasetMethod]
    const url = config.buildDatasetUrl(config.baseUrl, 'list_contact', {})

    const payload = {
      search: {
        list_id: listId,
        is_archived: false
      }
    }

    return this.apiCall(accountGuid, url, method, payload)
  }

  /**
   * Preview a blast
   */
  static previewBlast(
    accountGuid: string,
    blastId: number,
    contactId: number,
    sequenceId: number,
    campaignId: number,
    listId: number
  ): Promise<any> {
    const config = this.getConfig()
    const url = `${config.baseUrl}/api/services/preview`

    const payload = {
      message_id: blastId,
      contact_id: contactId,
      skip_tracking: true,
      sequence_data: {
        id: sequenceId,
        campaign_id: campaignId,
        options: {
          list_id: listId
        }
      }
    }

    return this.apiCall(accountGuid, url, HttpMethod.POST, payload)
  }

  /**
   * Send test blast email
   */
  static sendTestBlast(
    accountGuid: string,
    html: string,
    subject: string,
    preview: string,
    messageId: number,
    contactId: number,
    subscriberId: number,
    email: string
  ): Promise<void> {
    const config = this.getConfig()
    const url = `${config.baseUrl}/api/services/send/rendered`

    const payload = {
      html,
      subject,
      preview,
      message_id: messageId,
      contact_id: contactId,
      subscriber_id: subscriberId,
      email
    }

    return this.apiCall(accountGuid, url, HttpMethod.POST, payload)
  }

  /**
   * Fetch analytics data from campaigns API
   */
  static fetchAnalytics(accountGuid: string, payload: any): Promise<any> {
    const config = this.getConfig()
    const analyticsUrl = `${config.baseUrl}/api/services/analytics`
    const method = HttpMethod.POST

    return this.apiCall(accountGuid, analyticsUrl, method, payload)
  }

  /**
   * Fetch account info from campaigns API
   */
  static fetchAccountInfo(accountGuid: string): Promise<any> {
    const config = this.getConfig()
    const url = `${config.baseUrl}/api/entity/account/find`
    const payload = { guid: accountGuid }

    return this.apiCall(accountGuid, url, HttpMethod.POST, payload)
  }
}
